/*
 * From http://www.redblobgames.com/x/1745-rotmg-maps-webgl/
 * Copyright 2017 Red Blob Games <redblobgames@gmail.com>
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *      http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use strict';

const SimplexNoise = require('simplex-noise');
const DualMesh =     require('@redblobgames/dual-mesh');
const createMesh =   require('@redblobgames/dual-mesh/create');
const Map =          require('@redblobgames/mapgen2');
const DrawWater =    require('../1742-webgl-mapgen2/draw-water');
const Lighting =     require('../1742-webgl-mapgen2/lighting');
const {makeRandInt, makeRandFloat} = require('@redblobgames/prng');


let param = {
    seed: 24,
    variant: 0,
    spacing: 10,
    temperature: 0,
    rainfall: 0,
    canvasSize: 1000,
};

Lighting.param.exponent = 3;
Lighting.param.em.d = 0.5;
Lighting.param.em.e = 0;
Lighting.param.em.f = 0;
Object.assign(Lighting.param.drape,
              {slope: 10, flat: 10,
               distort_a: 0.0, distort_f: 2,
               noise_a: 0.3, noise_f: 10,
               scale_z: 0.8, rotate_x: 3.4, rotate_z: 0,
               c: 0.035, d: 30,
               outline_depth: 2.5, outline_strength: 10, outline_threshold: 0.025,});

console.time('mesh-init');
let mesh = new DualMesh(createMesh({spacing: param.spacing, random: makeRandFloat(12345)}));
console.timeEnd('mesh-init');
    
let canvas = document.createElement('canvas');
canvas.width = canvas.height = param.canvasSize;

let GLOBAL_canvas2;
let GLOBAL_r_water;
let GLOBAL_s_flow;

function readImage(mesh) {
    let source = document.getElementById('source');
    let canvas = document.createElement('canvas');
    canvas.width = 1000;
    canvas.height = 1000;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(source, 0, 0, 1000, 1000);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;

    function isBlue(r, darkOnly) {
        let mapX = mesh.r_x(r), mapY = mesh.r_y(r);
        let canvasX = Math.floor(mapX / 1000 * canvas.width),
            canvasY = Math.floor(mapY / 1000 * canvas.height);
        let p = 4 * (canvasY * canvas.width + canvasX);
        if (darkOnly) {
            return 128 < pixels[p+2] && pixels[p+2] < 150 && pixels[p+2] > 30+pixels[p+1];
        } else {
            return pixels[p+2] > 30+pixels[p+1] && pixels[p] < 128;
        }
    }
    
    /* NOTE:
       1. Dark blue is lakes or ocean
       2. Light blue is rivers or ocean
       I want to find all lakes and ocean but not rivers, so I first
       make a pass to find all ocean, then in the second pass any dark
       blue becomes r_water and light blue becomes rivers (s_flow).
    */

    let r_ocean = [];
    r_ocean.length = mesh.numRegions;
    r_ocean.fill(false);
    let stack = [mesh.ghost_r()];
    let r_out = [];
    while (stack.length > 0) {
        let r1 = stack.pop();
        mesh.r_circulate_r(r_out, r1);
        for (let r2 of r_out) {
            if (isBlue(r2, false) && !r_ocean[r2]) {
                r_ocean[r2] = true;
                stack.push(r2);
            }
        }
    }

    let r_water = [];
    r_water.length = mesh.numRegions;
    let s_flow = [];
    s_flow.length = mesh.numSides;
    s_flow.fill(0);
    let s_out = [];
    for (let r = 0; r < mesh.numRegions; r++) {
        r_water[r] = r_ocean[r] || mesh.r_boundary(r) || mesh.r_ghost(r) || isBlue(r, true);
        if (!r_ocean[r] && isBlue(r, false)) { // river
            mesh.r_circulate_s(s_out, r);
            for (let s of s_out) {
                s_flow[s] = 1;
            }
        }
    }

    /* Now that we've gotten the data we wanted from the canvas, 
     * alter it to keep the blue channel. This will let some of
     * the smooth colormap through, but still show the rivers and
     * grasslands that the new colors don't show */
    for (let y = 0; y < 1000; y++) {
        for (let x = 0; x < 1000; x++) {
            let p = 4 * (y * 1000 + x);
            pixels[p+3] = (100 +  pixels[p+2])/2;
        }
    }
    ctx.putImageData(imageData, 0, 0);
    
    GLOBAL_r_water = r_water;
    GLOBAL_s_flow = s_flow;
    GLOBAL_canvas2 = canvas;
}

// Monkeypatching - read data from GLOBAL_*
require('@redblobgames/mapgen2/water').assign_r_water = (r_water, mesh) => {
    for (let r = 0; r < mesh.numRegions; r++) { r_water[r] = GLOBAL_r_water[r]; }
};
require('@redblobgames/mapgen2/rivers').assign_s_flow = (s_flow, mesh) => {
    for (let s = 0; s < mesh.numSides; s++) { s_flow[s] = GLOBAL_s_flow[s]; }
};


function draw() {
    console.time('map-init');
    let map = new Map(mesh, {amplitude: 0.2, length: 4, seed: 12345}, makeRandInt);
    console.timeEnd('map-init');
    
    let ctx = canvas.getContext('2d');
    readImage(map.mesh);

    console.time('map-gen');
    map.calculate({
        noise: new SimplexNoise(makeRandFloat(param.seed)),
        numRivers: 0,
        drainageSeed: param.variant,
        riverSeed: param.variant,
        biomeBias: {temperature: param.temperature, moisture: param.rainfall},
    });
    console.timeEnd('map-gen');

    ctx.save();
    ctx.scale(canvas.width / 1000, canvas.height / 1000);
    ctx.clearRect(0, 0, 1000, 1000);
    
    console.time('draw-lakes');
    DrawWater.lakes(ctx, map);
    console.timeEnd('draw-lakes');
    
    console.time('draw-rivers');
    // DrawWater.rivers(ctx, map); // TODO: I'm not reading the river data correctly yet for rendering purposes, only for biome purposes
    console.timeEnd('draw-rivers');

    ctx.restore();
    
    //Lighting.draw(map, canvas);
    Lighting.draw(map, GLOBAL_canvas2);
}

function waitForImage() {
    if (!document.getElementById('source').complete) {
        requestAnimationFrame(waitForImage);
    } else {
        draw();
    }
}
waitForImage();

function makeMenu() {
    let select = document.getElementById('select-image');
    for (let i = 1; i <= 13; i++) {
        let option = document.createElement('option');
        option.setAttribute('value', i);
        if (i === 2) option.setAttribute('selected', "selected");
        option.appendChild(document.createTextNode("world " + i));
        select.appendChild(option);
    }
    select.addEventListener('change', () =>
                            {
                                document.getElementById('source').src="images/world" + select.value + ".png";
    waitForImage();
                            });
}
makeMenu();

Lighting.datGUI.close();
